import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Users, 
  BookOpen, 
  Search, 
  Filter, 
  CheckCircle, 
  User,
  FileText,
  Target
} from 'lucide-react';
import { toast } from '@/components/ui/sonner';

interface BulkAssignmentInterfaceProps {
  users: any[];
  templates: any[];
  onBulkAssign: (templateId: string, userIds: string[]) => void;
}

const BulkAssignmentInterface: React.FC<BulkAssignmentInterfaceProps> = ({
  users,
  templates,
  onBulkAssign
}) => {
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isAssigning, setIsAssigning] = useState(false);

  const handleUserSelect = (userId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
    }
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      setSelectedUsers(filteredUsers.map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleBulkAssign = async () => {
    if (!selectedTemplate || selectedUsers.length === 0) {
      toast.error('Please select both a template and users');
      return;
    }

    setIsAssigning(true);
    try {
      await onBulkAssign(selectedTemplate, selectedUsers);
      setSelectedUsers([]);
      setSelectedTemplate('');
    } catch (error) {
      console.error('Error in bulk assignment:', error);
    } finally {
      setIsAssigning(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'approved' && user.is_approved) ||
                         (filterStatus === 'pending' && !user.is_approved);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const selectedTemplate_obj = templates.find(t => t.id === selectedTemplate);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Bulk Assignment Interface</h3>
          <p className="text-sm text-muted-foreground">
            Assign courses to multiple users simultaneously
          </p>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline">
            {selectedUsers.length} user(s) selected
          </Badge>
          {selectedTemplate && (
            <Badge variant="secondary">
              Template: {selectedTemplate_obj?.name}
            </Badge>
          )}
        </div>
      </div>

      {/* Template Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Select Assignment Template
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <Card 
                key={template.id} 
                className={`cursor-pointer transition-all hover:shadow-md ${
                  selectedTemplate === template.id ? 'ring-2 ring-primary' : ''
                }`}
                onClick={() => setSelectedTemplate(template.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="h-4 w-4 text-primary" />
                    <h4 className="font-medium">{template.name}</h4>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    {template.description}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    <Badge variant="outline" className="text-xs">
                      {template.courses.length} courses
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {template.difficulty}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* User Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Select Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex gap-4 items-center mb-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-32">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="student">Students</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Select All */}
          <div className="flex items-center space-x-2 mb-4 p-2 bg-muted rounded">
            <Checkbox
              id="select-all"
              checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
              onCheckedChange={handleSelectAll}
            />
            <Label htmlFor="select-all" className="text-sm font-medium">
              Select all {filteredUsers.length} users
            </Label>
          </div>

          {/* User List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredUsers.map((user) => (
              <div 
                key={user.id} 
                className="flex items-center space-x-3 p-3 border rounded hover:bg-muted/50"
              >
                <Checkbox
                  id={`user-${user.id}`}
                  checked={selectedUsers.includes(user.id)}
                  onCheckedChange={(checked) => handleUserSelect(user.id, checked as boolean)}
                />
                <User className="h-8 w-8 text-muted-foreground" />
                <div className="flex-1">
                  <h4 className="font-medium">{user.name}</h4>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                </div>
                <div className="flex gap-1">
                  <Badge variant="outline">{user.role || 'student'}</Badge>
                  <Badge variant={user.is_approved ? "default" : "secondary"}>
                    {user.is_approved ? "Approved" : "Pending"}
                  </Badge>
                </div>
              </div>
            ))}
            
            {filteredUsers.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No users found matching the current filters
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Assignment Summary & Action */}
      {(selectedUsers.length > 0 || selectedTemplate) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Assignment Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {selectedTemplate_obj && (
                <div className="p-4 bg-muted rounded">
                  <h4 className="font-medium mb-2">Selected Template:</h4>
                  <p className="text-sm"><strong>{selectedTemplate_obj.name}</strong></p>
                  <p className="text-sm text-muted-foreground">{selectedTemplate_obj.description}</p>
                  <div className="flex gap-2 mt-2">
                    <Badge variant="outline">{selectedTemplate_obj.courses.length} courses</Badge>
                    <Badge variant="outline">{selectedTemplate_obj.estimatedDuration}</Badge>
                    <Badge variant="outline">{selectedTemplate_obj.difficulty}</Badge>
                  </div>
                </div>
              )}
              
              <div className="p-4 bg-muted rounded">
                <h4 className="font-medium mb-2">Selected Users:</h4>
                <p className="text-sm">{selectedUsers.length} user(s) selected</p>
                {selectedUsers.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {selectedUsers.slice(0, 5).map(userId => {
                      const user = users.find(u => u.id === userId);
                      return user ? (
                        <Badge key={userId} variant="secondary" className="text-xs">
                          {user.name}
                        </Badge>
                      ) : null;
                    })}
                    {selectedUsers.length > 5 && (
                      <Badge variant="outline" className="text-xs">
                        +{selectedUsers.length - 5} more
                      </Badge>
                    )}
                  </div>
                )}
              </div>

              <Button 
                onClick={handleBulkAssign}
                disabled={!selectedTemplate || selectedUsers.length === 0 || isAssigning}
                className="w-full"
                size="lg"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {isAssigning ? 'Assigning...' : `Assign to ${selectedUsers.length} User(s)`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BulkAssignmentInterface;